// src/hooks/useStrategyWebSocket.js

import { useState, useEffect, useRef } from 'react';

const STRATEGY_SERVER_URL = 'wss://api.hedger.bar/strategy/';

export const useStrategyWebSocket = (strategyParams) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('Idle');
  const ws = useRef(null);

  useEffect(() => {
    // Если параметры не заданы, ничего не делаем.
    if (!strategyParams) {
      if (ws.current) {
        console.log("Params removed. Closing connection.");
        ws.current.close();
        ws.current = null;
      }
      setStatus('Idle');
      return;
    }

    // Создаем новое соединение
    setStatus('Connecting');
    setData(null);
    
    const socket = new WebSocket(STRATEGY_SERVER_URL);
    ws.current = socket;

    socket.onopen = () => {
      if (ws.current === socket) {
        console.log('✅ WebSocket Open. Sending params...');
        // Отправляем параметры стратегии
        socket.send(JSON.stringify(strategyParams));
      }
    };

    socket.onmessage = (event) => {
      if (ws.current !== socket) return;

      try {
        const message = JSON.parse(event.data);

        // 1. Обработка статусов (например, "Loading history...")
        if (message.type === 'status') {
            if (message.message.includes('Loading')) setStatus('Loading History');
            else if (message.message.includes('Subscribed')) setStatus('Live');
            else setStatus(message.message);
            return;
        }

        // 2. Обработка SNAPSHOT (Полная история при старте)
        if (message.type === 'snapshot') {
            setData(message);
            setStatus('Live');
            return;
        }

        // 3. Обработка UPDATE (Новые данные в реальном времени)
        if (message.type === 'update') {
            setData((prevData) => {
                // Если у нас еще нет исторических данных, обновление применить нельзя
                if (!prevData) return null;

                // Формируем новую точку для графика Z-Score
                // API может присылать value или z, страхуемся
                const newZPoint = { 
                    timestamp: message.zPoint.timestamp, 
                    value: message.zPoint.value ?? message.zPoint.z ?? 0
                };

                return {
                    ...prevData, // Копируем все старые данные
                    
                    // Обновляем сводку и информацию об открытой сделке
                    summary: message.summary,
                    openTrade: message.openTrade,

                    // ДОБАВЛЯЕМ новую точку в график, не удаляя старые
                    zScoresData: [
                        ...(prevData.zScoresData || []), 
                        newZPoint
                    ],

                    // ДОБАВЛЯЕМ новые завершенные сделки (если есть)
                    completedTrades_raw: [
                        ...(prevData.completedTrades_raw || []),
                        ...(message.closedTrades || [])
                    ],
                    
                    // Обновляем свечи (добавляем новые в конец массива)
                    candlesA: [...(prevData.candlesA || []), message.candleA],
                    candlesB: [...(prevData.candlesB || []), message.candleB],
                };
            });
        }

      } catch (error) {
        console.error("Failed to parse message:", error);
      }
    };

    socket.onerror = (error) => {
      if (ws.current === socket) {
        console.error('WebSocket Error:', error);
        setStatus('Error');
      }
    };
    
    socket.onclose = () => {
      if (ws.current === socket) {
        console.log('❌ WebSocket Closed.');
        setStatus('Closed');
        ws.current = null;
      }
    };

    return () => {
      console.log('Cleanup: Closing WebSocket.');
      socket.close();
    };

  }, [strategyParams]);

  return { data, status };
};